import { useState } from 'react'
import { useCallback, useMemo, useRef } from 'react'
import { useService } from '../../../hooks/use-service'
import { NotificationService } from '../../../services'
import { CompositeImage, INotificationFieldSuggestions } from '../../../interfaces/notification-field-suggestions'
import { DomainDto } from '../../../dtos/domain'
import { GenerateNotificationVariantsRequest } from '@pushly/models/lib/structs'
import { useCompositeImageLoader } from './use-composite-image-loader'
import { NotificationVariantModel } from '../../../models/notification/notification-variant.model'
import { pause } from '@pushly/aqe/lib/utils'

export type PushlyAiApi = {
    readonly isGenerating: boolean
    generateNotificationVariants(req: NotificationVariantModel): Promise<INotificationFieldSuggestions | null>
    generateNotificationVariantsAdvanced(req: NotificationVariantModel): Promise<INotificationFieldSuggestions[] | null>
    removeVariantFromCache(variantIndex: number): void
}

type PageAnalysisDomain = Pick<DomainDto, 'id' | 'mediaQuality'>

const buildResponse = (data: INotificationFieldSuggestions | null): INotificationFieldSuggestions | null => {
    if (!data || Object.values(data).every((v) => (Array.isArray(v) ? !v.length : v === null))) return null
    return data
}

const buildAdvancedResponse = (
    data: INotificationFieldSuggestions[] | null,
): INotificationFieldSuggestions[] | null => {
    if (!data || data.length === 0) return null
    return data.filter((variant) => !Object.values(variant).every((v) => (Array.isArray(v) ? !v.length : v === null)))
}

export const usePushlyAi = ({ id: domainId, mediaQuality = 40 }: PageAnalysisDomain): PushlyAiApi => {
    if (!domainId) throw new Error('usePushlyAi: Domain ID could not be determined')

    const notifSvc = useService(NotificationService)

    const [isGenerating, setIsGenerating] = useState(false)
    const lastReqRef = useRef<GenerateNotificationVariantsRequest | null>(null)
    const resultsCacheRef = useRef<INotificationFieldSuggestions[]>([])

    const loadCompositeImage = useCompositeImageLoader({ id: domainId, mediaQuality })

    const generateNotificationVariants = useCallback(
        async (source: NotificationVariantModel): Promise<INotificationFieldSuggestions | null> => {
            const loadingKey = 'pushly-ai.generateNotificationVariants'

            try {
                if (isGenerating) return buildResponse(null)
                setIsGenerating(true)

                if (resultsCacheRef.current.length) {
                    await pause(500)
                    return buildResponse(resultsCacheRef.current.pop()!)
                }

                const sourceContent = source.getContent()

                const req = GenerateNotificationVariantsRequest.from({
                    flow: 'legacy',
                    domain_id: domainId,
                    segment_id: source.getAudience().getSegmentIds()?.[0]!,
                    num_variations: 4,
                    fewshot_max_days: 7,
                    content: {
                        landing_url: sourceContent.getDefaultContent().getLandingUrl()!,
                        /**
                         * ? Implementation Note ?
                         * v1 Pushly AI in pufferfish does not support generating variants
                         * with custom content. We are leaving this code here for future iterations.
                         */
                        // title: sourceContent.getDefaultContent().getTitle() ?? undefined,
                        // body: sourceContent.getDefaultContent().getBody() ?? undefined,
                        // keywords: sourceContent.getDefaultContent().getKeywords() ?? undefined,
                    },
                })

                // When the user has exhausted all previous results and clicks on "Generate" again
                // without changing the source content, we want to skip caching and force a new generation.
                const skipCache = Boolean(lastReqRef.current?.equals(req))
                console.debug('skip_cache', lastReqRef.current, req)

                lastReqRef.current = req.clone()
                req.skip_cache = skipCache

                const res = await notifSvc.generateNotificationVariants(domainId, req, {
                    showLoadingScreen: false,
                    cancellationKey: loadingKey,
                })

                if (!res.ok || !res.data) return buildResponse(null)

                resultsCacheRef.current = res.data
                    .map<INotificationFieldSuggestions>((item) => {
                        return {
                            title: item.title,
                            body: item.body,
                            imageUrl: item.image,
                            compositeImage: null,
                            keywords: item.keywords,
                        }
                    })
                    .reverse()

                const next = resultsCacheRef.current.pop() ?? null
                if (next?.imageUrl) {
                    let compositeImage = await loadCompositeImage(next.imageUrl)
                    if (compositeImage) {
                        next.compositeImage = compositeImage
                        next.imageUrl = compositeImage.url
                    }
                }

                console.debug('[usePushlyAi] resultsCache', { resultsCacheRef: resultsCacheRef.current })
                return buildResponse(next)
            } finally {
                setIsGenerating(false)
            }
        },
        [domainId, mediaQuality, loadCompositeImage],
    )

    const generateNotificationVariantsAdvanced = useCallback(
        async (source: NotificationVariantModel): Promise<INotificationFieldSuggestions[] | null> => {
            const loadingKey = 'pushly-ai.generateNotificationVariantsAdvanced'

            try {
                if (isGenerating) return buildAdvancedResponse(null)
                setIsGenerating(true)

                const sourceContent = source.getContent()

                const req = GenerateNotificationVariantsRequest.from({
                    flow: 'legacy',
                    domain_id: domainId,
                    segment_id: source.getAudience().getSegmentIds()?.[0]!,
                    num_variations: 4,
                    fewshot_max_days: 7,
                    content: {
                        landing_url: sourceContent.getDefaultContent().getLandingUrl()!,
                        title: sourceContent.getDefaultContent().getTitle() ?? undefined,
                        body: sourceContent.getDefaultContent().getBody() ?? undefined,
                        keywords: sourceContent.getDefaultContent().getKeywords() ?? undefined,
                    },
                })

                // For advanced mode, always skip cache to get fresh results
                req.skip_cache = true

                const res = await notifSvc.generateNotificationVariants(domainId, req, {
                    showLoadingScreen: false,
                    cancellationKey: loadingKey,
                })

                if (!res.ok || !res.data) return buildAdvancedResponse(null)

                // Preload images once for each returned image URL.
                const variantCompositeImageMap: Record<string, CompositeImage> = {}
                const uqVariantImageURLs = Array.from(
                    new Set(res.data.filter((item) => !!item.image).map(({ image }) => image!)),
                )
                await Promise.all(
                    uqVariantImageURLs.map(async (imageUrl) => {
                        try {
                            const compositeImage = await loadCompositeImage(imageUrl)
                            if (compositeImage) {
                                variantCompositeImageMap[imageUrl] ??= compositeImage
                            }
                        } catch {
                            console.warn('[usePushlyAi] Failed to preload composite image', imageUrl)
                        }
                    }),
                )

                // Convert GeneratedNotificationContent to INotificationFieldSuggestions with tone/reason
                const allVariants = await Promise.all(
                    res.data.map<Promise<INotificationFieldSuggestions & { tone?: string; reason?: string }>>(
                        async (item) => {
                            let variantCompositeImage: CompositeImage | null = null
                            if (item.image && variantCompositeImageMap[item.image]) {
                                variantCompositeImage = variantCompositeImageMap[item.image]
                            }

                            const variant: INotificationFieldSuggestions & { tone?: string; reason?: string } = {
                                title: item.title,
                                body: item.body,
                                imageUrl: variantCompositeImage?.url ?? item.image,
                                compositeImage: variantCompositeImage,
                                keywords: item.keywords,
                                tone: item.tone,
                                reason: item.reason,
                            }

                            return variant
                        },
                    ),
                )

                console.debug('[usePushlyAi] generateNotificationVariantsAdvanced', { allVariants })
                return buildAdvancedResponse(allVariants)
            } finally {
                setIsGenerating(false)
            }
        },
        [domainId, mediaQuality, loadCompositeImage],
    )

    const removeVariantFromCache = useCallback((variantIndex: number) => {
        // Remove the variant at the specified index from the cache
        // This is used when a user selects a variant from the advanced UI
        if (variantIndex >= 0 && variantIndex < resultsCacheRef.current.length) {
            resultsCacheRef.current.splice(variantIndex, 1)
            console.debug('[usePushlyAi] removeVariantFromCache', {
                variantIndex,
                remainingCache: resultsCacheRef.current,
            })
        }
    }, [])

    return useMemo(
        () => ({
            generateNotificationVariants,
            generateNotificationVariantsAdvanced,
            removeVariantFromCache,
            isGenerating,
        }),
        [generateNotificationVariants, generateNotificationVariantsAdvanced, removeVariantFromCache, isGenerating],
    )
}
